import { ReactElement, SVGProps } from 'react';

import {
  IconBuilding,
  IconClipboard,
  IconDots,
  IconFileText,
  IconGearOutline,
  IconHelpingHand,
  IconMultipleUsersOutline,
  IconPackageOpen,
  IconPresentationOutline,
  IconRefreshCcw,
} from '@iwoca/icons';
import { useLingui } from '@lingui/react/macro';
import { useFormContext } from 'react-hook-form';

import { useLocale } from '../../../../hooks/useLocale';
import { LOAN_PURPOSE_OPTIONS_DE } from '../../../GetStarted/GetStartedDE/loanOptions';
import { TMobileSignupForm } from '../../form/form';
import { useStageFieldTracking } from '../../form/MobileSignupFieldTracking';
import { useMobileSignupNavContext } from '../../hooks/useMobileSignupNavContext';
import { OptionCards, TOption } from '../../OptionCards/OptionCards';
import { StepLayout } from '../../StepLayout/StepLayout';

type TPurpose = (typeof LOAN_PURPOSE_OPTIONS_DE)[number]['value'];

type TIcon = (props: SVGProps<SVGSVGElement>) => ReactElement;

const PURPOSE_ICONS: Record<TPurpose, TIcon> = {
  stock_purchase: IconPackageOpen,
  bridging_loan: IconHelpingHand,
  pay_bill: IconFileText,
  equipment_purchase: IconGearOutline,
  financing_client_order: IconClipboard,
  pay_tax_bill: IconBuilding,
  refinancing_debt: IconRefreshCcw,
  pay_staff: IconMultipleUsersOutline,
  marketing: IconPresentationOutline,
  other: IconDots,
};

// DIT-678. Purpose picker. On a fresh visit selecting a card advances to the
// dedicated purpose-details step (where the detail is required for "other",
// optional otherwise); once answered (e.g. on back-nav) the CTA is shown so the
// user isn't forced to re-tap their existing choice to move on. Reads/writes the
// shared mobile signup form store (DIT-664).
export const PurposeScreen = () => {
  const { t } = useLingui();
  const { i18n } = useLocale();
  const { watch, setValue } = useFormContext<TMobileSignupForm>();
  const { goNext } = useMobileSignupNavContext();
  const { fieldProps } = useStageFieldTracking();

  const loanPurpose = watch('loanPurpose');

  const options: TOption<TPurpose>[] = LOAN_PURPOSE_OPTIONS_DE.map((option) => {
    const Icon = PURPOSE_ICONS[option.value];
    return {
      value: option.value,
      icon: <Icon width={24} height={24} aria-hidden />,
      label: i18n._(option.label),
    };
  });

  return (
    <StepLayout
      stageTitle={t`Credit request`}
      heading={t`What do you want to use the loan for?`}
      hideCta={!loanPurpose}
      onContinue={goNext}
      fieldProps={fieldProps}
    >
      <OptionCards
        name="loanPurpose"
        options={options}
        value={(loanPurpose as TPurpose) || undefined}
        onChange={(value) => {
          const isFirstChoice = !loanPurpose;
          setValue('loanPurpose', value, { shouldDirty: true });
          // Auto-advance only on the first pick; once answered the CTA drives
          // navigation, so re-selecting just updates the choice.
          if (isFirstChoice) goNext();
        }}
      />
    </StepLayout>
  );
};
